import {AttrsSchema, Component, ConfigOptions} from 'panel';

import {reporter} from 'common/report/error-reporting';
import {executeRecaptchaPromise} from 'common/util/recaptcha';
import {isValidEmailAddress} from 'common/util/string';
import {purgeStorage} from 'common/report/purge-storage';
import {ValidationStatus} from 'common/components/input/types';
import {IronContextRegistry} from 'common/report/context-registry';
import {apiClient} from 'common/util/api-client';
import {objToFormData} from 'common/util';
import {isLocalhostDevEnv} from 'common/report/util/env';

import {verifySsoGivenEmail} from '../api';
import {track, TrackEvents, TrackProperties} from '../tracking';
import {template} from './template';
import './index.styl';

interface FormValues {
  email: string;
  password: string;
  socialCredential: string;
}

type FormErrors = Partial<FormValues>;

interface SignInState {
  formValues: FormValues;
  formErrors: FormErrors;
  errorReason?: string;
  isSsoEnabled: boolean;
  isSsoRequired: boolean;
  isSocialSignIn: boolean;
  showPasswordField: boolean;
  waitingForResponse: boolean;
  showMagicLinkConfirmation: boolean;
  magicLinkEmail: string;
  notification: string; // Successful toast to show
  error: string; // Error toast to show
  next: string;
}

interface SignInAttrs {
  'recaptcha-key': string;
  email: string;
  // in attrSchema it is a `json` but it's actually a string from django
  error: string | null;
  'info-message': string;
  next: string;
  'enable-google-sign-in': boolean;
  'enable-magic-link': boolean;
}

const ErrorMessage = {
  required: {
    email: `Email is required.`,
    password: `Password is required.`,
  },
  format: {
    email: `Please enter a valid email address.`,
    password_too_long: `Password rules have changed: password can not be longer than 64 characters, please reset your password if it is longer than 64 characters.`,
  },
};

const CONTACT_SUPPORT_MSG = `Please log in to submit your support request`;

export class SignIn extends Component<SignInState, SignInAttrs, any, any, IronContextRegistry> {
  static get attrsSchema(): AttrsSchema<SignInAttrs> {
    return {
      ...super.attrsSchema,
      'recaptcha-key': {type: `string`, description: `secret key for recaptcha`},
      email: {type: `string`, description: `email address to login`},
      // this is either `"erorr message"` or `null` pass in by django
      error: {type: `json`, description: `possible login errors`},
      'info-message': {type: `string`, description: `possible notices`},
      next: {type: `string`, description: `next url`},
      'enable-google-sign-in': {type: `boolean`, description: `whether or not to display sign in with google button`},
      'enable-magic-link': {type: `boolean`, description: `whether or not to enable magic link signin`},
    };
  }

  get config(): ConfigOptions<SignInState, any, IronContextRegistry> {
    return {
      template,
      contexts: [`localStorageManager` as const],
      defaultState: {
        isSocialSignIn: false,
        showPasswordField: false, // Default to not showing password field for streamlined login
        waitingForResponse: false,
        showMagicLinkConfirmation: false,
        magicLinkEmail: null,
        error: null,
        notification: null,
        isSsoEnabled: false,
        isSsoRequired: false,
        next: ``,
        formValues: {
          email: ``,
          password: ``,
          socialCredential: ``,
        },
        formErrors: {},
      },
      helpers: {
        getSignInWithGoogleClientId: () => {
          // For some reason, the technique of doing $helpers.CONSTANT_NAME
          // doesn't work with webpack env specific globals like this one...
          return SIGN_IN_WITH_GOOGLE_CLIENT_ID;
        },
        getLoginButtonLabel: () => {
          return this.state.isSsoEnabled ? `Log in with password` : `Log in`;
        },
        handleEmailInput: (ev) => {
          const email = ev.target.value;
          this.updateFormValues({email});
          if (!this.state.isSocialSignIn && isValidEmailAddress(email)) {
            this.checkEmailForSSO(email);
          }
        },
        handleEmailBlur: () => {
          this.validateEmail(this.state.formValues.email);
        },
        getValidationStatus: (field) => {
          const {formErrors} = this.state;

          if (field in formErrors) {
            return formErrors[field] ? ValidationStatus.Invalid : ValidationStatus.Valid;
          }
          return ValidationStatus.Default;
        },
        handlePasswordInput: (ev) => {
          this.updateFormValues({password: ev.target.value});
        },
        handleSsoSignInBtnClick: (ev: MouseEvent) => {
          ev.preventDefault();
          ev.stopPropagation();

          // The trigger for the SSO flow is that no password is passed
          this.updateFormValues({password: ``});
          this.submitLoginForm(true); // async, no need to wait
        },
        handleSignInBtnClick: (ev: MouseEvent) => {
          ev.preventDefault();
          ev.stopPropagation();

          this.submitLoginForm(false); // async, no need to wait
        },
        handleToastClosed: () => {
          this.update({notification: null});
        },
        handleContinueBtnClick: (ev: MouseEvent) => {
          ev.preventDefault();
          ev.stopPropagation();

          this.handleMagicLinkContinue();
        },
        handleFormSubmit: (ev: Event) => {
          // prevent default submission and rely on async methods to perform the actual submission
          ev.preventDefault();
          // if user copies an invlaid email, we should validate if user hit enter as well
          this.validateEmail(this.state.formValues.email);
          if (Object.values(this.state.formErrors).some(Boolean)) {
            return;
          }

          if (!this.state.showPasswordField) {
            // check magic link flow case upon pressing enter
            this.helpers.handleContinueBtnClick(ev);
            return;
          }

          let useSSO: boolean = this.state.isSsoEnabled;

          // if both SSO and Password logins are available
          if (this.state.isSsoEnabled && !this.state.isSsoRequired) {
            // make a decision based on which input is focused
            const focusedInput: HTMLInputElement = this.el.querySelector(`input.mp-input:focus`);
            if (focusedInput && focusedInput.name === `password`) {
              useSSO = false;
            }
          }

          if (useSSO || this.state.isSocialSignIn) {
            // The trigger for the SSO flow is that no password is passed
            this.updateFormValues({password: ``});
          }

          this.submitLoginForm(useSSO); // async, no need to wait
        },
        handleCreatePasswordClick: () => {
          track(TrackEvents.ClickSetPassword);
        },
        handleContactSupportClick: () => {
          track(TrackEvents.ClickContactSupport);
        },
      },
    };
  }

  connectedCallback(): void {
    super.connectedCallback();
    purgeStorage(this.getContext(`localStorageManager`));

    if (this.attr(`next`)) {
      const stateUpdate: Partial<SignInState> = {
        next: this.attr(`next`),
      };
      if (stateUpdate.next.includes(`contact-us`) && !this.attr(`error`) && !this.state.errorReason) {
        stateUpdate.errorReason = CONTACT_SUPPORT_MSG;
      }
      if (!stateUpdate.next.includes(`#`) && window.location?.hash) {
        stateUpdate.next += window.location.hash;
      }
      // replace mutiple "##" with single "#"; remove trailing "#"
      stateUpdate.next = stateUpdate.next.replace(/#+/g, `#`).replace(/#$/, ``);
      this.update({...stateUpdate});
    }

    if (this.attr(`email`)) {
      this.updateFormValues({email: this.attr(`email`)});
      // Handle case where incorrect password is entered or another non-magic-link
      // login failure occurs for non-SSO customers
      if (!this.state.isSsoEnabled) {
        this.handleMagicLinkContinue();
      }
    }

    track(TrackEvents.ViewSignInPage, {
      [TrackProperties.PageVersion]: `Sign In Page`,
    });
  }

  updateFormValues(newValues: Partial<FormValues>) {
    const {formValues} = this.state;
    this.update({formValues: {...formValues, ...newValues}});
  }

  updateFormErrors(newErrors: Partial<FormErrors>): void {
    const {formErrors} = this.state;
    this.update({formErrors: {...formErrors, ...newErrors}});
  }

  async checkEmailForSSO(email: string) {
    if (email) {
      const response = await verifySsoGivenEmail(email);
      this.update(response);
    }
  }

  validateEmail(email?: string): void {
    if (!this.state.isSocialSignIn) {
      if (!email?.trim()) {
        this.updateFormErrors({email: ErrorMessage.required.email});
        return;
      }

      if (!isValidEmailAddress(email)) {
        this.updateFormErrors({email: ErrorMessage.format.email});
        return;
      }
    }
    this.updateFormErrors({email: ``});
  }

  validatePassword(password, isSSOLogin) {
    if (!isSSOLogin && !password?.trim()) {
      return this.updateFormErrors({password: ErrorMessage.required.password});
    }
    if (password?.trim().length > 64) {
      return this.updateFormErrors({password: ErrorMessage.format.password_too_long});
    }
    return this.updateFormErrors({password: ``});
  }

  handleGoogleCredentialResponse(response) {
    const gidInput: any = document.getElementById(`gid-input`);
    gidInput.value = response.credential;
    this.update({isSocialSignIn: true});
    // use isSSOLogin = true flag to disable password validation
    this.submitLoginForm(true);
  }

  async handleMagicLinkContinue() {
    if (this.state.waitingForResponse) {
      // We need to perform this check because the button is still clickable while disabled=true
      return;
    }
    this.update({waitingForResponse: true});

    // Get a valid email from the form
    const emailFormInput: any = document.querySelector(`#id_email`);
    const email = this.state.formValues.email || emailFormInput.value;
    this.validateEmail(email);
    if (Object.values(this.state.formErrors).some(Boolean)) {
      this.update({waitingForResponse: false});
      return;
    }

    // Check the user's login flow, to determine whether they should see password or get magic link
    // For now, if get_login_flow fails, let's assume the user is password, since most are
    let flow = `password`;
    try {
      const results = await apiClient.get<string>(`/get_login_flow/`, {email});
      flow = results[`loginFlow`];
    } catch (err) {
      this.update({
        // TODO: What's the UX like if the get_login_flow fails?
        error: `There has been an error fetching your login flow. Try turning off any ad blockers`,
      });
    }

    if (flow === `password`) {
      // We proceed as normal from here
      this.update({showPasswordField: true});
    } else {
      // User is magic link signup - send magic link and show confirmation
      const formData = objToFormData(
        Object.assign(
          {
            email,
            isRegistration: false,
          },
          this.state.next ? {next: this.state.next} : null,
        ),
      );
      try {
        await apiClient.post<string>(`/send_magic_link/`, formData);
        if (this.state.showMagicLinkConfirmation) {
          // We are on the confirm page, resending the link - show a toast
          this.update({
            notification: `We sent you another link. Please check your inbox.`,
          });
        } else {
          this.update({
            showMagicLinkConfirmation: true,
            magicLinkEmail: email,
          });
        }
      } catch (err) {
        this.update({
          error: err.message,
        });
      }
    }
    this.update({waitingForResponse: false});
  }

  /**
   *
   * @param isSSOLogin used to determine whether or not a password is required
   */
  async submitLoginForm(isSSOLogin = false) {
    // for acceptance and smoke tests to work
    // they directly update #id_email and #id_password's value
    const emailFormInput: any = document.querySelector(`#id_email`);
    const passwordFormInput: any = document.querySelector(`#id_password`);
    const email = this.state.formValues.email || emailFormInput.value;

    // SSO logins don't use a password
    const password = isSSOLogin ? `` : this.state.formValues.password || passwordFormInput.value;

    this.validateEmail(email);
    this.validatePassword(password, isSSOLogin);

    if (Object.values(this.state.formErrors).some(Boolean)) {
      return;
    }

    const isTestEmail = email === `pat@mixpanel.com` || !!email.match(/^testing+.*@example\.com/g);
    // bypass recaptcha for social sign-in
    if (!this.state.isSocialSignIn && !isTestEmail && !isLocalhostDevEnv()) {
      try {
        await executeRecaptchaPromise();
      } catch (err) {
        reporter.error(err);
        this.update({errorReason: `Recaptcha failed. Please refresh this page and retry.`});
        return;
      }
    }

    (document.getElementById(`mpSignInForm`) as HTMLFormElement).submit();
  }
}

customElements.define(`mp-sign-in`, SignIn);
